import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, cldOptimize } from "../lib/api";
import SharePG from "../components/SharePG";
import { useAuth } from "../context/AuthContext";
import BookingModal from "../components/BookingModal";
import { MapPin, Star, Heart, Wifi, ShieldCheck, ArrowLeft, Send, Bed, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [message, setMessage] = useState("Hi, I'm interested in this PG. Is it available?");
  const [moveIn, setMoveIn] = useState("");
  const [sending, setSending] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [saved, setSaved] = useState(false);
  const [bookingFor, setBookingFor] = useState(null); // {property, room|null}
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [complaint, setComplaint] = useState({ subject: "", description: "", priority: "medium" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${id}`);
      setP(data);
      if (user && typeof user === "object") {
        const saves = await api.get("/saved").catch(()=>({data:[]}));
        setSaved(saves.data.some(s => s.id === id));
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user]);

  const sendEnquiry = async () => {
    if (!user || typeof user !== "object") { nav("/login"); return; }
    setSending(true);
    try {
      await api.post("/enquiries", { property_id: id, message, move_in_date: moveIn || null });
      toast.success("Enquiry sent — the owner will reach out soon.");
      setMessage("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send enquiry");
    } finally { setSending(false); }
  };

  const submitReview = async () => {
    if (!user || typeof user !== "object") { nav("/login"); return; }
    try {
      await api.post("/reviews", { property_id: id, rating: reviewRating, comment: reviewText });
      setReviewText("");
      toast.success("Review posted!");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const toggleSave = async () => {
    if (!user || typeof user !== "object") { nav("/login"); return; }
    try {
      if (saved) { await api.delete(`/saved/${id}`); setSaved(false); toast("Removed from saved"); }
      else { await api.post(`/saved/${id}`); setSaved(true); toast.success("Saved!"); }
    } catch { toast.error("Try again"); }
  };

  const submitComplaint = async () => {
    if (!complaint.subject || !complaint.description) { toast.error("Fill subject and description"); return; }
    try {
      await api.post("/complaints", { ...complaint, property_id: id });
      toast.success("Complaint registered.");
      setComplaintOpen(false);
      setComplaint({ subject: "", description: "", priority: "medium" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-5 md:px-10 py-20">Loading...</div>;
  if (!p) return <div className="max-w-7xl mx-auto px-5 md:px-10 py-20">Not found.</div>;

  const imgs = p.images?.length ? p.images : ["https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200"];

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-8" data-testid="property-detail-page">
      <Link to="/properties" className="brut-tag white inline-flex items-center gap-1 mb-5" data-testid="back-to-list"><ArrowLeft size={14}/> Back</Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {/* Gallery */}
          <div className="brut-card overflow-hidden" data-testid="gallery">
            <div className="aspect-[16/10] bg-[#ECFAF1] overflow-hidden">
              <img src={cldOptimize(imgs[activeImg], "w_1400")} alt={p.title} className="w-full h-full object-cover"/>
            </div>
            {imgs.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {imgs.map((src, i) => (
                  <button key={i} onClick={()=>setActiveImg(i)} className={`w-24 h-16 flex-shrink-0 brut-border ${i===activeImg ? "brut-shadow-sm" : ""}`} data-testid={`gallery-thumb-${i}`}>
                    <img src={src} alt={`thumb ${i}`} className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 360° room tour */}
          {p.tour_url && (
            <div className="mt-4 rounded-2xl overflow-hidden border border-emerald-300 bg-white" data-testid="tour-360">
              <div className="bg-emerald-600 text-white px-4 py-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span>
                360° Room Tour · Walk through before you book
              </div>
              <div className="aspect-[16/10] bg-black">
                <iframe
                  title="360° room tour"
                  src={p.tour_url}
                  className="w-full h-full"
                  allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen"
                  loading="lazy"
                  data-testid="tour-iframe"
                />
              </div>
            </div>
          )}

          <div className="mt-8">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="brut-tag pink">{p.gender}</span>
              {p.college_nearby && <span className="brut-tag white">Near {p.college_nearby}</span>}
              {p.food_included && <span className="brut-tag lime">Food included</span>}
              {p.ac && <span className="brut-tag white">AC</span>}
              {p.avg_rating > 0 && <span className="brut-tag lime flex items-center gap-1"><Star size={12} fill="#0F1F17"/> {p.avg_rating}</span>}
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-3" data-testid="property-title">{p.title}</h1>
            <p className="flex items-center gap-1 text-[#4A4A4A] mt-2"><MapPin size={16}/> {p.address}</p>

            <h2 className="font-display font-black text-2xl mt-8">About this PG</h2>
            <p className="text-[#2a2a2a] leading-relaxed mt-2">{p.description}</p>

            <h2 className="font-display font-black text-2xl mt-8">Amenities</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3" data-testid="amenities-grid">
              {p.amenities?.map(a => (
                <div key={a} className="brut-border p-3 flex items-center gap-2 bg-white font-medium">
                  <Wifi size={16}/>{a}
                </div>
              ))}
            </div>

            {p.rooms?.length > 0 && (
              <>
                <h2 className="font-display font-black text-2xl mt-8">Available rooms & beds</h2>
                <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="rooms-grid">
                  {p.rooms.map(r => {
                  const left = r.available_beds ?? r.beds ?? 0;
                  const soldOut = left <= 0;
                  return (
                    <div key={r.id} className={`brut-card p-4 flex flex-col ${soldOut?"opacity-60":""}`} data-testid={`room-${r.id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-display font-black text-xl">{r.name}</div>
                          <div className="text-sm text-[#4A4A4A] mt-1 flex items-center gap-1"><Bed size={14}/> {r.sharing} · {soldOut ? <span className="text-[#FF90E8] font-bold">Sold out</span> : `${left} bed${left>1?"s":""} left`}</div>
                        </div>
                        <span className="brut-tag pink">₹{r.price_per_month}/mo</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {(r.furnishing || []).map(f => <span key={f} className="brut-tag white !text-[10px]">{f}</span>)}
                      </div>
                      <button onClick={()=>!soldOut && setBookingFor({property: p, room: r})} disabled={soldOut} className="brut-btn mt-4" data-testid={`book-room-${r.id}`}>
                        {soldOut ? "Sold out" : "Book this room"}
                      </button>
                    </div>
                  );
                })}
                </div>
              </>
            )}

            {p.house_rules?.length > 0 && (
              <>
                <h2 className="font-display font-black text-2xl mt-8">House rules</h2>
                <ul className="list-disc list-inside mt-2 space-y-1 text-[#2a2a2a]">
                  {p.house_rules.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </>
            )}

            {p.nearby_places?.length > 0 && (
              <>
                <h2 className="font-display font-black text-2xl mt-8">Nearby places</h2>
                <div className="flex flex-wrap gap-2 mt-3">
                  {p.nearby_places.map((n, i) => <span key={i} className="brut-tag white">{n}</span>)}
                </div>
              </>
            )}

            <h2 className="font-display font-black text-2xl mt-8">Location</h2>
            <div className="brut-card mt-3 aspect-[16/7] overflow-hidden" data-testid="map-placeholder">
              <iframe title="map" className="w-full h-full" src={`https://maps.google.com/maps?q=${encodeURIComponent(p.address)}&z=14&output=embed`} loading="lazy"/>
            </div>

            {/* Reviews */}
            <h2 className="font-display font-black text-2xl mt-10">Reviews ({p.reviews?.length || 0})</h2>
            <div className="space-y-3 mt-3">
              {p.reviews?.length ? p.reviews.map(r => (
                <div key={r.id} className="brut-card p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="font-bold">{r.user_name}</div>
                    <div className="brut-tag lime flex items-center gap-1"><Star size={10} fill="#0F1F17"/> {r.rating}</div>
                  </div>
                  <p className="mt-2 text-[#2a2a2a]">{r.comment}</p>
                </div>
              )) : <p className="text-[#4A4A4A]">No reviews yet. Be the first!</p>}
            </div>

            <div className="brut-card p-5 mt-5 bg-[#FFEAF6]" data-testid="review-form">
              <h3 className="font-display font-black text-xl">Write a review</h3>
              <div className="flex gap-2 mt-3">
                {[1,2,3,4,5].map(n => (
                  <button key={n} onClick={()=>setReviewRating(n)} className={`brut-border p-2 ${n<=reviewRating ? "bg-[#2ED573]" : "bg-white"}`} data-testid={`rating-${n}`}>
                    <Star size={16} fill={n<=reviewRating ? "#0F1F17" : "transparent"}/>
                  </button>
                ))}
              </div>
              <textarea className="brut-input mt-3 min-h-[80px]" value={reviewText} onChange={e=>setReviewText(e.target.value)} placeholder="Share your experience..." data-testid="review-textarea"/>
              <button className="brut-btn mt-3" onClick={submitReview} disabled={!reviewText} data-testid="submit-review-btn">Post review</button>
            </div>

            {user && typeof user === "object" && (
              <button onClick={()=>setComplaintOpen(!complaintOpen)} className="brut-btn pink mt-6" data-testid="raise-complaint-btn"><AlertTriangle size={14}/> Raise a complaint about this PG</button>
            )}
            {complaintOpen && (
              <div className="brut-card p-5 mt-3 bg-[#FFEAF6]" data-testid="complaint-form">
                <input className="brut-input" placeholder="Subject" value={complaint.subject} onChange={e=>setComplaint({...complaint, subject: e.target.value})} data-testid="complaint-subject"/>
                <textarea className="brut-input mt-2 min-h-[80px]" placeholder="Describe the issue..." value={complaint.description} onChange={e=>setComplaint({...complaint, description: e.target.value})} data-testid="complaint-desc"/>
                <select className="brut-input mt-2" value={complaint.priority} onChange={e=>setComplaint({...complaint, priority: e.target.value})}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
                <button className="brut-btn mt-3" onClick={submitComplaint} data-testid="submit-complaint-btn">Submit complaint</button>
              </div>
            )}
          </div>
        </div>

        {/* Sticky booking */}
        <aside className="lg:sticky lg:top-24 lg:h-fit space-y-4">
          <div className="brut-card p-6 bg-[#2ED573]" data-testid="price-card">
            <div className="text-xs font-bold uppercase tracking-wider">Starting from</div>
            <div className="font-display font-black text-4xl mt-1">₹{p.price_per_month?.toLocaleString()}<span className="text-base font-bold">/mo</span></div>
            <div className="text-sm mt-1">Owner: <strong>{p.owner_name}</strong></div>
            <button className="brut-btn dark w-full mt-4" onClick={()=>setBookingFor({property: p, room: null})} data-testid="book-now-btn">Book Now</button>
            <div className="mt-3"><SharePG property={p}/></div>
          </div>

          <div className="brut-card p-5" data-testid="enquiry-form">
            <h3 className="font-display font-black text-xl">Or send an enquiry</h3>
            <label className="label-eyebrow mt-4 block">Move-in date</label>
            <input type="date" className="brut-input mt-1" value={moveIn} onChange={e=>setMoveIn(e.target.value)} data-testid="enquiry-date"/>
            <label className="label-eyebrow mt-4 block">Message</label>
            <textarea className="brut-input mt-1 min-h-[100px]" value={message} onChange={e=>setMessage(e.target.value)} data-testid="enquiry-message"/>
            <button className="brut-btn w-full mt-4" onClick={sendEnquiry} disabled={sending || !message} data-testid="send-enquiry-btn">
              <Send size={16}/> {sending ? "Sending..." : "Send Enquiry"}
            </button>
            <button className="brut-btn white w-full mt-2" onClick={toggleSave} data-testid="save-btn">
              <Heart size={16} fill={saved ? "#FF90E8" : "transparent"}/> {saved ? "Saved" : "Save"}
            </button>
            <div className="text-xs text-[#4A4A4A] mt-3 flex items-start gap-2">
              <ShieldCheck size={14} className="flex-shrink-0 mt-0.5"/> Hostro never asks for advance payments outside the platform. Stay safe.
            </div>
          </div>
        </aside>
      </div>

      {bookingFor && <BookingModal property={bookingFor.property} room={bookingFor.room} onClose={()=>setBookingFor(null)}/>}
    </div>
  );
}
